/**
 * cd2bSiembra — QUÉ puede sembrar el asesor en una empresa, y con qué palabras se le dice.
 *
 * Los tres alcances existen en el servidor desde que se separó la CARTA de la OPERACIÓN
 * (`coredeskApp/src/nube-server/server.ts`, canal admin `sembrarDatosPrueba`), pero las dos
 * pantallas que siembran —el editor de la cotización y la ficha del cliente— ofrecían solo
 * dos. El tercero era inalcanzable: existía, estaba probado, y ningún asesor podía pedirlo.
 *
 * La AUTORIDAD de qué rubro tiene carta es el servidor, que rechaza con un error explícito
 * («El giro "X" no tiene una carta de plantilla») en vez de sembrar otra cosa a escondidas.
 * Este espejo solo evita ofrecer una opción que se va a rechazar.
 */

/** Rubros con carta de plantilla. Espejo de `GIROS_CON_CARTA` del nube-server. */
export const GIROS_CON_CARTA = ['restaurante']

/**
 * Rubros con inventario de cuartos de plantilla. Espejo de `GIROS_CON_HABITACIONES`.
 *
 * Es el equivalente hotelero de la carta, y se ofrece por lo mismo: sin esta entrada el alcance
 * quedaría probado en el servidor y ningún asesor podría pedirlo — que es exactamente el defecto
 * que este archivo vino a cerrar.
 */
export const GIROS_CON_HABITACIONES = ['hotel']

/**
 * Opciones del selector «Qué sembrar» para un giro.
 *
 * `carta` va PRIMERA en los rubros que la tienen y es la que hay que usar sobre la empresa
 * de un cliente REAL: le deja el menú puesto para editarlo y no le mete ni una venta, ni una
 * comanda, ni un DTE en unos libros que van a Hacienda.
 */
export function opcionesAlcanceSiembra(giro) {
  const opciones = []
  if (GIROS_CON_CARTA.includes(String(giro || '').trim())) {
    opciones.push({
      valor: 'carta',
      etiqueta: 'Solo la carta del rubro — para un cliente real',
      ayuda:
        'Deja el menú completo puesto y editable: categorías, insumos, platos con su receta, ' +
        'fotos y las estaciones de cocina. NO siembra ventas, comandas ni DTE, así que se puede ' +
        'usar sobre la empresa de un cliente que ya va a facturar. Tarda segundos.',
    })
  }
  if (GIROS_CON_HABITACIONES.includes(String(giro || '').trim())) {
    opciones.push({
      valor: 'habitaciones',
      etiqueta: 'Solo los cuartos del hotel — para un cliente real',
      ayuda:
        'Deja puestos los tipos de habitación con su capacidad y sus minutos de limpieza, los ' +
        'cuartos numerados por piso, el producto que factura la noche y las amenidades que se ' +
        'reponen al limpiar. Las reglas de tarifa por ocupación entran APAGADAS, para no cambiarle ' +
        'el precio a nadie. NO siembra estadías, folios ni DTE, así que se puede usar sobre la ' +
        'empresa de un hotel que ya factura. Tarda segundos.',
    })
  }
  opciones.push({
    valor: 'operacion',
    etiqueta: 'Operación completa (~12 meses) — la demo de verdad',
    ayuda:
      'Catálogo, clientes, proveedores, empleados y un año de ventas, compras, inventario y ' +
      'contabilidad del rubro. Tarda varios minutos. Es una DEMO: no la siembres sobre una ' +
      'empresa que ya factura de verdad.',
  })
  opciones.push({
    valor: 'catalogo',
    etiqueta: 'Solo catálogo de ejemplo — rápido',
    ayuda: 'Solo unos productos de muestra del rubro. Sirve para enseñar la pantalla, no para enseñar el negocio.',
  })
  return opciones
}

/** Texto de ayuda del alcance elegido (vacío si no corresponde a ninguno). */
export function ayudaAlcance(giro, alcance) {
  return opcionesAlcanceSiembra(giro).find((o) => o.valor === alcance)?.ayuda || ''
}
